import { TicketProps } from 'app/components/TicketCarousel/Ticket';
import { AllAttractionPropsFragment, Locale } from 'app/generated/hygraph';
import { AttractionContent } from 'app/hooks/useAttraction';
import { ProductContent } from 'app/hooks/useProduct';
import {
  AttractionConfig,
  EntitlementReservation,
  Pass,
  Ticket,
  TicketCategory,
  TicketClass,
  TicketType,
} from 'app/services/GuestCenterService.types';
import {
  getWalletValuesForAddon,
  ReservationTextHelper,
  TranslationHelper,
} from 'app/services/I18n';
import { getOptionContent, isUpgradeReservation } from 'app/services/ReservationHelper';
import { cdn } from 'app/utils/image';

export function isAddonTicket(ticket: Ticket): boolean {
  if (ticket.type !== null) {
    return ticket.type === TicketType.AddOn;
  }
  // Fallback for backward compatibility
  return ticket.isAddon ?? false;
}

/**
 * Gets the addon contentKey by looking it up in attractionConfig.addOns
 * Tries to match by familyCode otherwise use the first addon
 */
export function getAddonContentKey(
  attractionConfig: AttractionConfig | undefined,
  familyCode?: string
): string | undefined {
  const { ticketingSystem } = attractionConfig ?? {};
  const addOns = ticketingSystem?.addOns;

  if (!addOns) {
    return undefined;
  }

  // Try to find by familyCode
  if (familyCode) {
    const addon = addOns.find((addOn) => addOn.familyCode === familyCode);
    if (addon?.contentKey) {
      return addon.contentKey.toLowerCase();
    }
  }

  // Use the first addon as fallback
  if (addOns.length === 1) {
    const [firstAddon] = addOns;
    if (firstAddon?.contentKey) {
      return firstAddon.contentKey.toLowerCase();
    }
  }

  return undefined;
}

export function createTicket({
  ticket,
  pass,
  productContent: product,
  attractionContentKey,
  orderNumber,
  ticketCategory,
  attractionContent,
  attractionConfig,
  familyCode,
  t,
  rt,
}: {
  ticket: Ticket;
  pass?: Pass;
  productContent: ProductContent;
  reservation?: EntitlementReservation;
  orderNumber?: string;
  ticketCategory?: TicketCategory;
  attractionContentKey?: string;
  attractionContent?: AttractionContent;
  attractionConfig?: AttractionConfig;
  familyCode?: string;
  t?: TranslationHelper;
  rt?: ReservationTextHelper;
}): TicketProps {
  const isAddon = isAddonTicket(ticket);

  const attraction = product.attractions.find(
    (attr: AllAttractionPropsFragment) => attr.key === attractionContentKey
  );

  const productCodeTicketNames =
    product.ticketGroups?.reduce<Record<string, string>>(
      (acc, { key: groupKey, title, subtitle }) => {
        if (groupKey) {
          if (title && subtitle) {
            acc[groupKey] = `${title} ${subtitle}`;
          } else {
            acc[groupKey] = title;
          }
        }
        return acc;
      },
      {}
    ) || {};

  const ticketName = getTicketName(
    product.cityName?.value || product.name,
    pass?.productCode,
    productCodeTicketNames
  );

  const barcodeScan = ticket.scan ?? ticket.barcode ?? '';
  const barcodeDisplay = ticket.display ?? ticket.barcode ?? barcodeScan;

  // Get wallet values for addon tickets
  let walletGuestValue: string | undefined;
  let walletTicketValue: string | undefined;
  let ticketBadgeValue: string | undefined;

  if (isAddon && attractionContent && t) {
    const addonContentKey = getAddonContentKey(attractionConfig, familyCode);
    const walletValues = getWalletValuesForAddon({
      attractionContent,
      addonContentKey,
      product,
      t,
    });
    walletGuestValue = walletValues.walletGuestValue;
    walletTicketValue = walletValues.walletTicketValue;
  }

  // Get badge value for addon tickets using rt() helper (same system as other reservation keys)
  if (isAddon && rt) {
    ticketBadgeValue = rt('addon_ticket_badge_value');
  }

  const barcodeLogoAsset = getBarcodeLogoAsset({ attraction, ticket });

  return {
    ageRange: isAddon
      ? null
      : (pass?.ticketClass === TicketClass.Adult ? product.ageAdult : product.ageChild) ?? null,
    barcodeLogo: barcodeLogoAsset?.url
      ? cdn(barcodeLogoAsset.url, [['output', { format: 'png' }]])
      : '',
    barcodeDisplay,
    barcodeScan,
    guestName: isAddon ? walletGuestValue ?? 'Complimentary' : pass?.name ?? '',
    isFree: isAddon,
    logo: {
      height: product.logo?.height ?? 1,
      url: product.logo?.url ?? '',
      width: product.logo?.width ?? 1,
    },
    orderNumber,
    ticketCategory,
    ticketClass: isAddon ? undefined : pass?.ticketClass,
    ticketName: isAddon && walletTicketValue ? walletTicketValue : ticketName,
    ticketBadgeValue,
    walletGuestValue,
    walletTicketValue,
  };
}

function getBarcodeLogoAsset({
  attraction,
  ticket,
}: {
  attraction?: AllAttractionPropsFragment;
  ticket: Ticket;
}) {
  const matchedBarcodeLogo = getUpgradeBarcodeLogo({ attraction, ticket });

  return matchedBarcodeLogo?.logo ?? attraction?.barcodeLogo ?? null;
}

function getUpgradeBarcodeLogo({
  attraction,
  ticket,
}: {
  attraction?: AllAttractionPropsFragment;
  ticket: Ticket;
}) {
  if (!ticket.upgradeContentKey) {
    return undefined;
  }

  const hygraphUpgradeKey = `upgrade_${ticket.upgradeContentKey}`;
  return attraction?.barcodeLogos?.find(
    (barcodeLogo) => barcodeLogo.key.toLowerCase() === hygraphUpgradeKey.toLowerCase()
  );
}

export function getUpgradeReservationAttractionNames({
  reservations,
  attractions,
}: {
  reservations: EntitlementReservation[];
  attractions: AllAttractionPropsFragment[];
}): string[] {
  const names = reservations
    .filter(isUpgradeReservation)
    .map(
      (reservation) =>
        attractions.find((a) => a.key === reservation.attractionConfig.contentKey)?.name ?? ''
    )
    .filter(Boolean);

  return Array.from(new Set(names));
}

export function createReservationTicket({
  attractionContent,
  reservation,
  ticket,
  rt,
  ticketCategory,
}: {
  attractionContent: AttractionContent;
  reservation: EntitlementReservation;
  ticket?: Ticket;
  rt: ReservationTextHelper;
  ticketCategory?: TicketCategory;
}) {
  const upgradeTimeText = ticket
    ? getUpgradeBarcodeLogo({ attraction: attractionContent, ticket })?.label?.value
    : undefined;
  return {
    reservation,
    attractionName: attractionContent.name ?? '',
    dateTime: reservation.reservationDateTime,
    timeNote: upgradeTimeText ?? undefined,
    option: getOptionContent(reservation, { type: 'title', rt, ticketCategory, locale: Locale.En }),
    referenceNumber: reservation.display,
    type: ticketCategory?.reservationType ?? reservation.type,
    ticketCategory,
  };
}

function getTicketName(
  cityName?: Maybe<string>,
  productCode?: Maybe<string>,
  productCodeTicketNames?: Record<string, string>
) {
  const productCodeTicketName = productCode && productCodeTicketNames?.[productCode];
  return productCodeTicketName ?? `${cityName ?? ''} ${productCode ?? 'CityPASS'}`;
}

/**
 * Gets the addon label for travel party display (used in showOrderBarcode mode).
 * Looks up the label from Hygraph via the addon contentKey.
 * Falls back to "Free" if no label is found.
 */
export function getAddonLabel({
  attractionConfig,
  attractionContent,
  product,
  familyCode,
  t,
}: {
  attractionConfig?: AttractionConfig;
  attractionContent?: AttractionContent;
  product?: ProductContent;
  familyCode?: string;
  t: TranslationHelper;
}): string {
  const addonContentKey = getAddonContentKey(attractionConfig, familyCode);

  if (attractionContent && addonContentKey) {
    const walletValues = getWalletValuesForAddon({
      attractionContent,
      addonContentKey,
      product,
      t,
    });
    // Use walletGuestValue as the addon label, fallback to "Free"
    if (walletValues.walletGuestValue) {
      return walletValues.walletGuestValue;
    }
  }

  return 'Free';
}
